import { useState, useEffect, useContext } from 'react';
import { Checkbox, Box, Grid, FormControlLabel } from '@mui/material';
import { Security } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFormText,
    SttButtonSave,
    SttPage,
} from '../../sporttia/all';

type User = {
    id?: number;
    name?: string;
    email?: string;
    twoFactorEnabled?: boolean;
};

export default function Account(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    // Set page header title
    useEffect(() => {
        cxt.setHeader({ title: t('account.myAccount') });
    }, []);

    const [user, setUser] = useState<User>({});
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const twoFAEnabled = user?.twoFactorEnabled ?? false;

    /**
     * Change password
     */
    const changePassword = () => {
        // Same password ???
        if (password === password2) {
            // Update password
            cxt?.api('PUT', '/me', {
                params: {
                    password,
                },
                success: () => {
                    cxt.showMessage('S', t('account.passwordSaved'));
                },
            });
        } else {
            cxt?.showMessage('E', t('account.passwordsDontMatch'));
        }
    };

    const post2FA = (is2FAEnabled: boolean) => {
        cxt?.api('PUT', '/me', {
            params: {
                twoFactorEnabled: is2FAEnabled,
            },
            success: (r: { user: User }) => {
                setUser(r.user);
                cxt.showMessage('S', t('user.enabled2FA'));
            },
            error: (r: any) => {
                if (r?.error?.msg) cxt.showMessage('E', r.error.msg);
            },
        });
    };

    /**
     * Load user data.
     */
    const loadUserData = () => {
        cxt?.api('GET', `/me`, {
            success: (r: { user: User }) => {
                setUser(r.user);
            },
        });
    };

    /**
     * Init
     */
    useEffect(() => {
        loadUserData();
    }, []);

    // -----| Render |-----
    return (
        <SttPage>
            <Box p={5}>
                <Grid container spacing={3}>
                    <Grid size={{ md: 6 }}>
                        <b>
                            {t('account.adminPassword')} ({cxt?.user?.name})
                        </b>
                        <br />
                        <br />

                        <SttFormText
                            grid
                            sm={12}
                            md={12}
                            xs={12}
                            password
                            name="password"
                            caption={t('account.newPassword')}
                            defVal={password}
                            onChange={({
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => setPassword(value as string)}
                        />

                        <br />

                        <SttFormText
                            grid
                            sm={12}
                            md={12}
                            xs={12}
                            password
                            name="password2"
                            caption={t('account.repeatPassword')}
                            defVal={password2}
                            onChange={({
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => setPassword2(value as string)}
                        />
                        <br />
                        <SttButtonSave
                            fullWidth
                            caption={t('account.changePassword')}
                            disabled={!password}
                            onClick={() => changePassword()}
                        />
                    </Grid>
                    <Grid size={{ md: 6 }}>
                        <b>{t('user.label2FA')}</b>
                        <br />
                        <Box display="flex" alignItems="center" gap={1}>
                            <Security color="action" />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={twoFAEnabled ?? false}
                                        onChange={() => {
                                            post2FA(!twoFAEnabled);
                                        }}
                                    />
                                }
                                label={t('user.label2FA')}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </SttPage>
    );
}
